// src/pages/SubscriberCheckoutPage.jsx
// Route: /subscriber/checkout
// Expects location.state = { plan, order } from SubscriberPlanPage's
// createPlanOrder() call. Redirects back to /subscriber/plan if that state
// is missing (e.g. a direct navigation or page refresh) — never invents an
// order client-side.
//
// order.provider decides the branch: 'STRIPE' mounts a Payment Element
// (AED, Guardian Dubai subscribers); 'RAZORPAY' opens the existing
// Checkout.js modal via services/razorpay.js (INR, unchanged flow). Amount/
// currency/gateway were all already resolved server-side when the order was
// created — this page never re-derives them.

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SubscriberAppShell } from '../components/SubscriberLayout';
import { useSubscriber } from '../context/SubscriberContext';
import { subscriberAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { getStripe } from '../services/stripe';
import { openRazorpayCheckout } from '../services/razorpay';

function StripeCheckout({ order, plan, subscriberId, onVerified, onError }) {
  const containerRef = useRef(null);
  const stripeRef = useRef(null);
  const elementsRef = useRef(null);
  const paymentElementRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stripe = await getStripe();
        if (cancelled || !containerRef.current) return;
        const elements = stripe.elements({ clientSecret: order.clientSecret });
        const paymentElement = elements.create('payment');
        paymentElement.mount(containerRef.current);
        paymentElementRef.current = paymentElement;
        stripeRef.current = stripe;
        elementsRef.current = elements;
        setReady(true);
      } catch (err) {
        onError(err.message || 'Could not load Stripe checkout.');
      }
    })();
    return () => {
      cancelled = true;
      // Explicitly tell Stripe to detach its DOM (an iframe it inserted
      // directly into containerRef, outside React's own tracking) BEFORE
      // React's reconciler tries to clean up this same div. Without this,
      // React and Stripe both think they own the container's children,
      // which throws "removeChild ... not a child of this node" the moment
      // React tries to unmount/remount it — happens on every StrictMode dev
      // double-invoke, and on a normal route change away from checkout.
      if (paymentElementRef.current) {
        try { paymentElementRef.current.unmount(); } catch { /* already detached */ }
        paymentElementRef.current = null;
      }
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!stripeRef.current || !elementsRef.current || submitting) return;
    setSubmitting(true);
    try {
      const { error, paymentIntent } = await stripeRef.current.confirmPayment({
        elements: elementsRef.current,
        redirect: 'if_required',
      });
      if (error) {
        onError(error.message || 'Payment failed.');
        setSubmitting(false);
        return;
      }
      await onVerified({ gatewayOrderId: paymentIntent.id });
    } catch (err) {
      onError(err.message || 'Payment failed.');
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {!ready && (
        <div style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>Loading payment form…</div>
      )}
      {/* Stripe mounts its Payment Element (an iframe) directly into this
          div — it must never contain any React-rendered children, or React
          and Stripe both try to manage its contents. See the effect above's
          cleanup for the matching paymentElement.unmount() call. */}
      <div ref={containerRef} style={{ marginBottom: 20, minHeight: ready ? 'auto' : 0 }} />
      <button
        type="submit"
        disabled={!ready || submitting}
        className="btn btn-accent btn-lg"
        style={{ width: '100%' }}
      >
        {submitting ? 'Confirming payment\u2026' : `Pay AED ${(order.amountMinorUnits / 100).toLocaleString()}`}
      </button>
    </form>
  );
}

function RazorpayCheckout({ order, plan, subscriber, onVerified, onError }) {
  const [opened, setOpened] = useState(false);

  async function handlePay() {
    if (opened) return;
    setOpened(true);
    try {
      const response = await openRazorpayCheckout({
        amount: order.amountMinorUnits,
        currency: order.currency,
        name: 'Previu Guardian',
        description: `${plan === 'GUARDIAN_SENTINEL' ? 'Guardian Sentinel' : 'Guardian'} plan`,
        orderId: order.gatewayOrderId,
        prefill: {
          name: `${subscriber?.firstName || ''} ${subscriber?.lastName || ''}`.trim(),
          email: subscriber?.email,
        },
        themeColor: 'var(--text-primary)',
      });
      if (!response) { setOpened(false); return; } // user dismissed
      await onVerified({
        gatewayOrderId: response.razorpay_order_id,
        gatewayPaymentId: response.razorpay_payment_id,
        gatewaySignature: response.razorpay_signature,
      });
    } catch (err) {
      onError(err.message || 'Payment failed.');
      setOpened(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handlePay}
      disabled={opened}
      className="btn btn-accent btn-lg"
      style={{ width: '100%' }}
    >
      {opened ? 'Waiting for payment\u2026' : `Pay \u20b9${(order.amountMinorUnits / 100).toLocaleString()}`}
    </button>
  );
}

export default function SubscriberCheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { subscriber, refreshSubscriber } = useSubscriber();
  const { toast } = useToast();
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const state = location.state;

  useEffect(() => {
    if (!state?.order) {
      navigate('/subscriber/plan', { replace: true });
    }
  }, [state, navigate]);

  if (!state?.order) return null;
  const { plan, householdSize, order } = state;

  async function handleVerified(verifyPayload) {
    setVerifying(true);
    try {
      const verifyRes = await subscriberAPI.verifyPlanOrder(subscriber.subscriberId, { plan, householdSize, ...verifyPayload });
      // Update the cached subscriber profile so the dashboard's
      // "no active plan" banner disappears without a full reload.
      //
      // 31 Jul 2026 — fixed a real bug here: householdSize was never
      // included in this update, so it stayed stale (undefined) after every
      // purchase. That silently broke SubscriberPlanPage's isCurrent check
      // (`householdSize === subscriber.householdSize` compared a real
      // string against undefined), which made the just-purchased plan's
      // tile fall through to the "upgrade" branch and compute a bogus
      // AED 0 delta instead of showing as disabled with "✓ Your current
      // plan". Also now caches the real subscriptionRenewalAt from the
      // verify response instead of leaving it stale too.
      const updated = {
        ...subscriber,
        subscriptionStatus: 'ACTIVE',
        subscriptionPlan: plan,
        householdSize,
        subscriptionRenewalAt: verifyRes.data?.subscriptionRenewalAt || subscriber.subscriptionRenewalAt,
        maxBeneficiaries: householdSize === 'COUPLE' ? 2 : 1,
      };
      localStorage.setItem('previu_subscriber', JSON.stringify(updated));
      refreshSubscriber();
      toast.success('Your plan is active!');
      navigate('/subscriber/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Payment succeeded but activation failed — contact your Navigator.');
      setVerifying(false);
    }
  }

  function handleError(message) {
    setError(message);
    toast.error(message);
  }

  return (
    <SubscriberAppShell breadcrumbs={[
      { label: 'My Family', to: '/subscriber/dashboard' },
      { label: 'Plan & Billing', to: '/subscriber/plan' },
      { label: 'Checkout' },
    ]}>
      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">Complete your payment</div>
        </div>
      </div>

      <div className="form-section fade-in-up" style={{ maxWidth: 480 }}>
        {error && (
          <div role="alert" className="alert alert-error">
            {error}
          </div>
        )}

        {verifying && (
          <div className="loading-state" style={{ padding: '20px 0' }}>
            <div className="spinner" />
            Confirming your subscription…
          </div>
        )}

        {!verifying && order.provider === 'STRIPE' && (
          <StripeCheckout order={order} plan={plan} subscriberId={subscriber?.subscriberId}
            onVerified={handleVerified} onError={handleError} />
        )}
        {!verifying && order.provider === 'RAZORPAY' && (
          <RazorpayCheckout order={order} plan={plan} subscriber={subscriber}
            onVerified={handleVerified} onError={handleError} />
        )}
      </div>
    </SubscriberAppShell>
  );
}
